import { ArrowLeft } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { AnyRouter } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

type Props = {
    className?: string
    /**
     * Đặt khi màn cần nói rõ back về đâu (hai màn lồng nhau back về hai nơi
     * khác nhau); bỏ trống thì dùng nhãn "Quay lại" dùng chung.
     */
    ariaLabel?: string
} & (
    | {
          /**
           * Màn nào đã có logic quay lại riêng (ví dụ về đúng danh sách cha sau
           * khi lưu) thì truyền vào đây; bỏ trống thì nút lùi một bước lịch sử,
           * tương đương `navigate(-1)`.
           */
          onBack?: () => void
          asChild?: false
          children?: never
      }
    | {
          /**
           * Dùng khi màn muốn giữ nguyên `<Link>` sẵn có làm đích quay lại
           * (giữ prefetch của router thay vì đổi sang điều hướng bằng tay).
           */
          asChild: true
          children: ReactNode
          onBack?: never
      }
)

export function PageBackButton({
    onBack,
    asChild,
    children,
    className,
    ariaLabel,
}: Props) {
    // `warn: false` để component còn dựng được ngoài RouterProvider (unit test);
    // ngoài đời luôn có router nên nhánh dưới mới là nhánh chạy thật.
    const router = useRouter({ warn: false }) as AnyRouter | undefined

    const handleBack = () => {
        if (onBack) {
            onBack()
            return
        }
        if (router) {
            // Mở thẳng bằng deep link / shortcut PWA thì không có bước nào để
            // lùi, back sẽ rơi ra ngoài app hoặc không làm gì — đưa về trang chủ.
            if (router.history.canGoBack()) {
                router.history.back()
            } else {
                void router.navigate({ to: '/' })
            }
            return
        }
        window.history.back()
    }

    return (
        <Button
            type={asChild ? undefined : 'button'}
            asChild={asChild}
            variant="ghost"
            size="icon"
            data-testid="page-back-button"
            aria-label={ariaLabel ?? m.common_aria_back()}
            className={cn('-ml-2 shrink-0', className)}
            onClick={asChild ? undefined : handleBack}
        >
            {asChild ? children : <ArrowLeft className="size-4" />}
        </Button>
    )
}
